import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CancellationError } from '../../../confirm/confirm-dialog.service';
import { HistoryState, IRadioEprEventEx } from '../../../models/patient-history.models';
import { selectSeReportIsAnyExaminationActive } from "../../../report/report.selectors";
import { RootState } from '../../../root-store/root.state';
import { FormType } from '../../../se-duration/se-duration.component';
import { seSearchSelect } from '../../../search-page/redux/search-page.actions';
import { AuthenticationService } from '../../../services/authentication/authentication.service';
import { ImagingService } from '../../../services/imaging/imaging.service';
import { NotificationService } from '../../../services/notification.service';
import { IEprEventsWithErrorMessage, PatientHistoryService } from '../../../services/patient-history.service';
import { ReportWorkflowService } from '../../../services/report-workflow.service';
import { PatientSettingsService } from '../../../services/settings/patient-settings.service';
import { fromServerIsoString } from '../../../utils/interop';
import { SeSideService } from '../../se-side.service';
import { SeSideComponentBase, SeSideComponentType } from '../se-side-components-models';
import { SeSideSnackbarComponent } from '../se-side-snackbar/se-side-snackbar.component';
import { SeSideSnackbarStateService } from '../services/se-side-snackbar-state.service';
import {
    SeSidePatientHistoryComponentActions,
    seSidePatientHistoryComponentNoResult,
    seSidePatientHistoryComponentSetPatient,
} from './redux/se-side-patient-history.actions';
import { SeSidePatientHistoryItemComponent } from './se-side-patient-history-item/se-side-patient-history-item.component';

@Component({
    selector: 'se-side-patient-history',
    templateUrl: './se-side-patient-history.component.html',
    styleUrls: ['./se-side-patient-history.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatTooltip,
        TranslateModule,
        SeSidePatientHistoryItemComponent,
        MatProgressSpinnerModule,
        SeSideSnackbarComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [SeSideSnackbarStateService]
})
export class SeSidePatientHistoryComponent extends SeSideComponentBase implements OnInit, OnDestroy {
    @HostBinding('style.display')
    actionsSubscription!: Subscription;
    activeExaminationSubscription!: Subscription;
    clinicalHistoryState = signal<HistoryState>(HistoryState.LOADING);
    enableAllBusinessUnitExaminationSearch: boolean = false;
    expandedItemId : string | null = null;
    isAllBUSearch = false;
    isAnyExaminationActive: boolean = false;
    items : IRadioEprEventEx[] = [];
    patientHistoryMessage : string = '';
    searchSelectSubscription!: Subscription;
    type: SeSideComponentType = SeSideComponentType.patientHistory;

    private currentPatientId: string = '';
    private formType?: FormType;
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    constructor (
        private translateService : TranslateService,
        private patientHistoryService : PatientHistoryService,
        private actions : Actions,
        private store : Store<RootState>,
        private reportWorkflowService : ReportWorkflowService,
        private notificationService : NotificationService,
        private imagingService: ImagingService,
        private patientSettingsService: PatientSettingsService,
        private authenticationService: AuthenticationService,
        private sideSnackbar: SeSideSnackbarStateService,
        private seSideService: SeSideService,
    ) {
        super();

        this.patientHistoryMessage = this.translateService.instant('NoResults');
        this.isLoading.next(false);
    }

    ngOnInit () {
        this.enableAllBusinessUnitExaminationSearch = this.patientSettingsService.enableAllBusinessUnitExaminationSearch() as boolean;
        this.listenForStoreEvents();
    }

    ngOnDestroy () {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();

        this.actionsSubscription.unsubscribe();
        this.searchSelectSubscription.unsubscribe();
        this.activeExaminationSubscription.unsubscribe();

        // Scarico eventuali immagini dal viewer
        this.imagingService.discardImages();

        this.formType = undefined;
    }

    onHistorySelectionChanged(selectedItem: IRadioEprEventEx) {
        this.items.forEach((item: any) => { // IRadioEprEventEx
            item.selected = (item.id == selectedItem.id);
        });
        if (this.expandedItemId === selectedItem.id) {
            this.expandedItemId = null;
        } else {
            this.expandedItemId = selectedItem.id;
        }
    }


    private listenForStoreEvents () {
        this.actionsSubscription = this.actions.pipe(ofType(
            seSidePatientHistoryComponentSetPatient,
            seSidePatientHistoryComponentNoResult)).subscribe(
                async a => {
                    this.isLoading.next(true);
                    this.clinicalHistoryState.set(HistoryState.LOADING);

                    this.formType = a.formType;

                    const notAvailableMsg = this.translateService.instant('ClinicalHistoryNotAvailable');
                    const noResultMsg = this.translateService.instant('NoResults');
                    const notCompleteMsg = this.translateService.instant('ClinicalHistoryNotComplete');

                    switch (a.type) {
                        case SeSidePatientHistoryComponentActions.SET_PATIENT:
                            if (!a.patientId) {
                                this.patientHistoryMessage = noResultMsg;
                                this.clinicalHistoryState.set(HistoryState.SUCCESS_NO_RESULT);
                                this.isLoading.next(false);
                                break;
                            }
                            this.currentPatientId = a.patientId;
                            this.patientHistoryMessage = this.translateService.instant('Loading');
                            try {
                                let isAllBuSearch: boolean = a.isAllBuSearch || false;

                                this.patientHistoryService.getAllEprEvents(a.patientId.toString(), isAllBuSearch, a.credentials)
                                    .pipe(takeUntil(this.ngUnsubscribe)) // blocca le eventuali chiamate in corso
                                    .subscribe({
                                        next: (result: IEprEventsWithErrorMessage) => {
                                            this.items = [];

                                            if (result.eprEvents && result.eprEvents.length > 0) {
                                                // Ci sono dei risultati
                                                this.patientHistoryMessage = '';

                                                // Aggiungo gli item trovati
                                                result.eprEvents.forEach((event) => {
                                                    const dateString = this.formatDate(event.date);
                                                    const reportDateString = this.formatDate(event.reportDate);

                                                    this.items.push({ ...event, selected: false, reportDateString: reportDateString, dateString: dateString });
                                                });

                                                // Se uno dei due messaggi di errore è presente, notifico
                                                if (!!result.displayMessage || !!result.exceptionMessage) {
                                                    let message : string = '';
                                                    message += result.displayMessage || '';
                                                    message += result.exceptionMessage ? ` - ${result.exceptionMessage}` : '';

                                                    // Non ci sono risultati - Notifica warning
                                                    this.notificationService.warning(message, notCompleteMsg);
                                                    this.setHistoryState(HistoryState.INCOMPLETE);
                                                }
                                                else {
                                                    // STORIA COMPLETA
                                                    this.setHistoryState(HistoryState.SUCCESS);
                                                }

                                                this.openLastApprovedReport();
                                            }
                                            else {
                                                this.patientHistoryMessage = noResultMsg;
                                                this.setHistoryState(HistoryState.SUCCESS_NO_RESULT);

                                            }

                                            this.isLoading.next(false);
                                        },
                                        error: err => {
                                            this.patientHistoryMessage = noResultMsg;

                                            if (err.data == null || err.data.exceptionTypeName !== 'Estensa.Web.Exceptions.CredentialsFault') {
                                                this.items = [];
                                                this.notificationService.warning(
                                                    (err.statusText) ? err.statusText : '', notAvailableMsg);
                                            }
                                            else if (err.data != null && err.data.exceptionTypeName == 'Estensa.Web.Exceptions.CredentialsFault') {

                                                // messaggio della credenziali
                                                let errorMessage = this.translateService.instant('Exception_CredentialsFault') +
                                                    ((err?.data?.exceptionMessage) ? ": " + err.data.exceptionMessage : '');
                                                this.notificationService.warning(errorMessage);
                                            }

                                            this.isLoading.next(false);
                                            this.setHistoryState(HistoryState.ERROR);
                                        }
                                    });
                            }
                            catch (error) {
                                if (!(error instanceof CancellationError)) {
                                    this.items = [];
                                    this.notificationService.warning(
                                    (error && error.message) ? error.message : '', notAvailableMsg);
                                }
                                this.patientHistoryMessage = noResultMsg;

                                // Notifica warning
                                this.notificationService.warning(
                                    (error && error.message) ? error.message : '');

                                this.isLoading.next(false);
                                this.setHistoryState(HistoryState.ERROR);
                            }
                            break;

                        case SeSidePatientHistoryComponentActions.NO_RESULT:
                            this.ngUnsubscribe.next();
                            this.patientHistoryMessage = a.message;
                            this.items = [];
                            this.isLoading.next(false);
                            this.setHistoryState(HistoryState.SUCCESS_NO_RESULT);
                            break;

                        default:
                            this.patientHistoryMessage = noResultMsg;
                            this.isLoading.next(false);
                            this.setHistoryState(HistoryState.ERROR);
                            break;
                    }
                });

        // Cambio di selezione in lista
        this.searchSelectSubscription = this.actions.pipe(ofType(seSearchSelect)).subscribe(() => {
            // Scarico eventuali immagini dal viewer
            this.imagingService.discardImages();
        });

        this.activeExaminationSubscription = this.store.select(selectSeReportIsAnyExaminationActive).subscribe((isAnyExaminationActive) => {
            // Metodo per capire se siamo in WL ( = false) o dentro una CP ( = true)
            this.isAnyExaminationActive = isAnyExaminationActive;
        });
    }

    private formatDate (date : string | Date | null) {
        if (!date) {
            return null;
        }
        if (date instanceof Date) {
            date = date.toJSON();
        }

        return fromServerIsoString(date)
            .toDate()
            .toLocaleDateString(
                undefined,
                { hour: '2-digit', minute: '2-digit' }
            );
    }
    async reLoadEprEventsRequired () {
        this.isAllBUSearch = true;
        //Recupero le credenziali
        let credentials = await this.reportWorkflowService.askAuthenticationConfirm(this.authenticationService.getUserId(), true);
        // Chiamare l'azione con i nuovi dati
        this.store.dispatch(
            seSidePatientHistoryComponentSetPatient({
                formType: this.formType,
                patientId: this.currentPatientId,
                isAllBuSearch: this.isAllBUSearch,
                credentials: credentials
            })
        );
    }

    private openLastApprovedReport() {
        let itemWithStateDorF = this.items.filter((item) => item.status.includes('D') || item.status.includes('F'));
        if (itemWithStateDorF.length > 0) {
            itemWithStateDorF.reduce((a, b) => a.date > b.date ? a : b);
            // Se ci sono esami con stato D o F quello con data più recente parte aperto
            this.onHistorySelectionChanged(itemWithStateDorF[0]);
        }
    }

    get clinicalHistoryTooltip(): string {
        switch (this.clinicalHistoryState()) {
            case HistoryState.INCOMPLETE:
            case HistoryState.ERROR:
                return this.translateService.instant(
                    'ClinicalHistoryFail'
                );
            case HistoryState.SUCCESS:
            case HistoryState.SUCCESS_NO_RESULT:
                return this.translateService.instant(
                    'ClinicalHistoryComplete'
                );
            default:
                return '';
        }
    }

    private setHistoryState(state: HistoryState) {
        this.clinicalHistoryState.set(state);
        this.seSideService.setTabError(
            'patientHistory',
            state === HistoryState.ERROR || state === HistoryState.INCOMPLETE
        );
        state == HistoryState.ERROR || state == HistoryState.INCOMPLETE
            ? this.sideSnackbar.show(this.translateService.instant('ClinicalHistoryFail'), 'error')
            : this.sideSnackbar.clear();
    }
}

